import { useState, useEffect } from 'react'
import { apiUrl, authFetch } from '../../lib/api'

interface Ticket {
  id: string
  subject: string
  status: string
  state: string
  priority: string
  category: string
  admin_reply?: string
  created_at: string
  updated_at: string
}

export function SupportPanel({ onClose }: { onClose?: () => void }) {
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [view, setView] = useState<'list' | 'new' | 'detail'>('list')
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null)
  const [loading, setLoading] = useState(true)
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [category, setCategory] = useState('general')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => { loadTickets() }, [])

  const loadTickets = async () => {
    setLoading(true)
    try {
      const res = await authFetch(apiUrl('/api/enterprise/my-tickets'))
      setTickets(await res.json())
    } catch {}
    setLoading(false)
  }

  const submitTicket = async () => {
    if (!subject.trim() || !message.trim()) return
    setSubmitting(true)
    try {
      await authFetch(apiUrl('/api/enterprise/ticket'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject, message, category }),
      })
      setSubmitted(true)
      setSubject(''); setMessage('')
      setTimeout(() => { setSubmitted(false); setView('list'); loadTickets() }, 2000)
    } catch {}
    setSubmitting(false)
  }

  if (view === 'detail' && selectedTicket) {
    return (
      <div style={s.root}>
        <div style={s.header}>
          <button onClick={() => { setView('list'); setSelectedTicket(null) }} style={s.backBtn}>&larr;</button>
          <span style={s.title}>{selectedTicket.subject}</span>
          <span style={{ ...s.badge, background: (selectedTicket.state || selectedTicket.status) === 'new' || (selectedTicket.state || selectedTicket.status) === 'open' ? 'rgba(245,161,87,0.15)' : (selectedTicket.state || selectedTicket.status) === 'resolved' || (selectedTicket.state || selectedTicket.status) === 'closed' ? 'rgba(95,212,164,0.15)' : 'rgba(56,189,248,0.15)', color: (selectedTicket.state || selectedTicket.status) === 'new' || (selectedTicket.state || selectedTicket.status) === 'open' ? '#f5a157' : (selectedTicket.state || selectedTicket.status) === 'resolved' || (selectedTicket.state || selectedTicket.status) === 'closed' ? '#5fd4a4' : '#38bdf8' }}>{selectedTicket.state || selectedTicket.status}</span>
        </div>
        <div style={s.body}>
          <div style={{ marginBottom: 12 }}>
            {[['Category', selectedTicket.category], ['Priority', selectedTicket.priority], ['State', selectedTicket.state || selectedTicket.status], ['Assigned To', selectedTicket.assigned_to || 'Pending assignment'], ['Submitted', new Date(selectedTicket.created_at).toLocaleString()], ['Updated', new Date(selectedTicket.updated_at).toLocaleString()]].map(([l, v]) => (
              <div key={l} style={{ display: 'flex', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <span style={{ width: 90, fontSize: 10, color: 'rgba(255,255,255,0.4)', fontFamily: 'ui-monospace, monospace' }}>{l}</span>
                <span style={{ fontSize: 11, color: '#e0e0e0' }}>{v}</span>
              </div>
            ))}
          </div>
          {selectedTicket.admin_reply && (
            <div style={{ padding: '12px 14px', borderRadius: 8, background: 'rgba(124,110,245,0.06)', border: '1px solid rgba(124,110,245,0.15)', marginBottom: 12 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: '#7c6ef5', marginBottom: 6 }}>SUPPORT TEAM REPLY</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{selectedTicket.admin_reply}</div>
            </div>
          )}
          {!selectedTicket.admin_reply && (selectedTicket.state === 'new' || selectedTicket.state === 'in_progress') && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', borderRadius: 8, background: selectedTicket.state === 'in_progress' ? 'rgba(56,189,248,0.04)' : 'rgba(245,161,87,0.04)', border: `1px solid ${selectedTicket.state === 'in_progress' ? 'rgba(56,189,248,0.15)' : 'rgba(245,161,87,0.15)'}`, fontSize: 11, color: selectedTicket.state === 'in_progress' ? '#38bdf8' : '#f5a157' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: selectedTicket.state === 'in_progress' ? '#38bdf8' : '#f5a157', animation: 'pulse-blink 1.5s infinite' }} />
              {selectedTicket.state === 'in_progress' ? 'Support team is working on your issue...' : 'Waiting for support team response...'}
            </div>
          )}
          {(selectedTicket.state === 'resolved' || selectedTicket.state === 'closed') && (
            <div style={{ padding: '12px 14px', borderRadius: 8, background: 'rgba(95,212,164,0.06)', border: '1px solid rgba(95,212,164,0.15)', fontSize: 12, color: '#5fd4a4', fontWeight: 700 }}>
              &#10003; This ticket has been {selectedTicket.state}
            </div>
          )}
        </div>
      </div>
    )
  }

  if (view === 'new') {
    return (
      <div style={s.root}>
        <div style={s.header}>
          <button onClick={() => setView('list')} style={s.backBtn}>&larr;</button>
          <span style={s.title}>New Support Ticket</span>
        </div>
        <div style={s.body}>
          {submitted ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ fontSize: 24, color: '#5fd4a4', marginBottom: 8 }}>&#10003;</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#5fd4a4' }}>Ticket Submitted!</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>We'll get back to you within 24 hours.</div>
            </div>
          ) : (
            <div style={{ maxWidth: 450 }}>
              <div style={s.formGroup}>
                <label style={s.label}>Category</label>
                <select value={category} onChange={e => setCategory(e.target.value)} style={s.select}>
                  {['general', 'bug', 'billing', 'ai', 'deployment', 'account', 'feature_request', 'performance'].map(c => (
                    <option key={c} value={c} style={{ background: '#1a1a2e', color: '#e0e0e0' }}>{c.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Subject</label>
                <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Brief description of the issue" style={s.input} />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Description</label>
                <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Describe the issue in detail. Include steps to reproduce, error messages, and what you expected to happen." rows={6} style={s.textarea} />
              </div>
              <button onClick={submitTicket} disabled={!subject.trim() || !message.trim() || submitting} style={{ ...s.submitBtn, opacity: (!subject.trim() || !message.trim() || submitting) ? 0.5 : 1 }}>
                {submitting ? 'Submitting...' : 'Submit Ticket'}
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div style={s.root}>
      <div style={s.header}>
        <span style={s.title}>Support</span>
        <div style={{ flex: 1 }} />
        <button onClick={() => setView('new')} style={s.newBtn}>+ New Ticket</button>
        {onClose && <button onClick={onClose} style={s.backBtn}>Close</button>}
      </div>
      <div style={s.body}>
        {loading && <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, textAlign: 'center', padding: 40 }}>Loading...</div>}
        {!loading && tickets.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>&#128172;</div>
            <div style={{ fontSize: 13, color: '#fff', fontWeight: 700, marginBottom: 4 }}>Need help?</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 16 }}>Submit a ticket and our team will respond within 24 hours.</div>
            <button onClick={() => setView('new')} style={s.newBtn}>Submit a Ticket</button>
          </div>
        )}
        {tickets.map(t => (
          <div key={t.id} onClick={() => { setSelectedTicket(t); setView('detail') }} style={s.card}
            onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(124,110,245,0.3)')}
            onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)')}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ ...s.badge, background: t.state === 'new' ? 'rgba(245,161,87,0.15)' : t.state === 'in_progress' ? 'rgba(56,189,248,0.15)' : 'rgba(95,212,164,0.15)', color: t.state === 'new' ? '#f5a157' : t.state === 'in_progress' ? '#38bdf8' : '#5fd4a4' }}>{t.state || t.status}</span>
              <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', padding: '2px 6px', background: 'rgba(255,255,255,0.03)', borderRadius: 4 }}>{t.category}</span>
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)' }}>{new Date(t.created_at).toLocaleDateString()}</span>
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#fff' }}>{t.subject}</div>
            {t.admin_reply && <div style={{ fontSize: 10, color: '#7c6ef5', marginTop: 4 }}>&#128172; Support replied</div>}
          </div>
        ))}
      </div>
      <style>{`@keyframes pulse-blink { 0%,100%{opacity:1} 50%{opacity:0} }`}</style>
    </div>
  )
}

const s: Record<string, React.CSSProperties> = {
  root: { height: '100%', display: 'flex', flexDirection: 'column' },
  header: { display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  title: { fontSize: 14, fontWeight: 700, color: '#fff' },
  backBtn: { background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, padding: '4px 10px', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: 11 },
  newBtn: { padding: '6px 14px', borderRadius: 6, border: 'none', background: '#7c6ef5', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' },
  body: { flex: 1, overflow: 'auto', padding: 18 },
  badge: { padding: '2px 8px', borderRadius: 4, fontSize: 9, fontWeight: 700, fontFamily: 'ui-monospace, monospace' },
  card: { padding: '12px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)', marginBottom: 8, cursor: 'pointer', transition: 'border-color 0.15s' },
  formGroup: { marginBottom: 14 },
  label: { display: 'block', fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.5)', marginBottom: 4, fontFamily: 'ui-monospace, monospace' },
  input: { width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.1)', background: '#1a1a2e', color: '#e0e0e0', fontSize: 12, fontFamily: 'ui-monospace, monospace', outline: 'none' },
  textarea: { width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.1)', background: '#1a1a2e', color: '#e0e0e0', fontSize: 12, fontFamily: 'ui-monospace, monospace', outline: 'none', resize: 'vertical' as const, lineHeight: 1.5 },
  select: { width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.1)', background: '#1a1a2e', color: '#e0e0e0', fontSize: 12, fontFamily: 'ui-monospace, monospace', cursor: 'pointer' },
  submitBtn: { padding: '10px 24px', borderRadius: 6, border: 'none', background: '#7c6ef5', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', marginTop: 8 },
}
